import { buttonStyle } from 'components/buttons/buttonStyle';
import { DataTable } from 'components/tables/data-table';
import type { DataTableColumnDef } from 'components/tables/data-table';
import { Button } from 'components/ui/button';
import { Skeleton } from 'components/ui/skeleton';
import { ChangeEvent, useMemo, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import {
    useGetCurrentTenantIconQuery,
    useRemoveTenantIconMutation,
    useUpdateTenantIconMutation,
} from 'store/api/tenants.ts';
import { TenantConfigurationDto } from 'types/tenants.ts';
import { t } from 'utils/getTranslations.ts';
import { useHasPermissions } from 'utils/permissionUtil';
import { readImageDimensions } from './CompanyLogoOnInvoiceControl';

const RECOMMENDED_ICON_SIZE = 128;
const MAX_ICON_SIZE = 1024;

interface Props {
    tenantConfiguration: TenantConfigurationDto;
}

async function validateIconFile(file: File): Promise<string | null> {
    const dimensions = file.type === 'image/png' ? await readImageDimensions(file) : null;
    if (!dimensions) {
        return t('configuration.tenant-icon-invalid-type');
    }
    if (dimensions.width !== dimensions.height) {
        return t('configuration.tenant-icon-not-square');
    }
    if (dimensions.width > MAX_ICON_SIZE) {
        return t('configuration.tenant-icon-invalid-dimensions', { maxSize: MAX_ICON_SIZE.toString() });
    }
    return null;
}

/**
 * The tenant's square icon, which the new design's sidebar shows in place of the tenant's initial: on the rail, and in
 * the open header when no company logo is set.
 */
export function TenantIconControl({ tenantConfiguration }: Props): JSX.Element {
    const hasCustomIcon = tenantConfiguration.hasCustomIcon;
    const { data: iconUrl, isLoading: isIconLoading } = useGetCurrentTenantIconQuery(undefined, {
        skip: !hasCustomIcon,
    });
    const [updateTenantIcon, { isLoading: isUploading }] = useUpdateTenantIconMutation();
    const [removeTenantIcon, { isLoading: isRemoving }] = useRemoveTenantIconMutation();
    const hasUpdatePermission = useHasPermissions(['UPDATE.BACK_OFFICE_SETTINGS']);
    const [validationError, setValidationError] = useState<string | null>(null);
    const isChangeDisabled = !hasUpdatePermission || isUploading || isRemoving;

    const columns = useMemo<DataTableColumnDef<TenantConfigurationDto>[]>(() => {
        const handleFileChange = async (event: ChangeEvent<HTMLInputElement>) => {
            const file = event.target.files?.[0];
            event.target.value = '';
            if (!file) {
                return;
            }
            const error = await validateIconFile(file);
            setValidationError(error);
            if (!error) {
                await updateTenantIcon(file);
            }
        };

        return [
            {
                header: t('configuration.tenant-icon'),
                id: 'tenant-icon',
                enableSorting: false,
                meta: { widthWeight: 2, minPx: 300 },
                cell: () => (
                    <div className="flex flex-col gap-2 py-2">
                        {/* The cached URL can outlive a removal: the flag says whether there is an icon. */}
                        {hasCustomIcon && iconUrl ? (
                            <img
                                src={iconUrl}
                                alt={t('configuration.tenant-icon')}
                                className="bg-paper outline-image-edge size-14 rounded-md object-cover outline -outline-offset-1"
                            />
                        ) : hasCustomIcon && isIconLoading ? (
                            <Skeleton className="size-14 rounded-md" />
                        ) : null}
                        <span className="text-caption text-fg-secondary">
                            {t('configuration.tenant-icon-requirements', { size: RECOMMENDED_ICON_SIZE.toString() })}
                        </span>
                        {validationError ? <span className="text-caption text-error">{validationError}</span> : null}
                        <div className="flex flex-row gap-2">
                            {/* A label, so a click on it opens the file input it holds. */}
                            <Button
                                {...buttonStyle(
                                    { variant: 'outlined' },
                                    'data-disabled:pointer-events-none data-disabled:opacity-50'
                                )}
                                nativeButton={false}
                                render={<label />}
                                disabled={isChangeDisabled}
                            >
                                <FormattedMessage id="configuration.tenant-icon-change" />
                                <input
                                    hidden
                                    type="file"
                                    accept=".png"
                                    disabled={isChangeDisabled}
                                    onChange={(event) => void handleFileChange(event)}
                                />
                            </Button>
                            <Button
                                {...buttonStyle({ variant: 'outlined' })}
                                disabled={isChangeDisabled || !hasCustomIcon}
                                onClick={() => {
                                    setValidationError(null);
                                    void removeTenantIcon();
                                }}
                            >
                                <FormattedMessage id="configuration.tenant-icon-remove" />
                            </Button>
                        </div>
                    </div>
                ),
            },
            {
                header: '',
                id: 'spacer',
                enableSorting: false,
                meta: { widthWeight: 3 },
                cell: () => null,
            },
        ];
    }, [hasCustomIcon, iconUrl, isIconLoading, validationError, isChangeDisabled, updateTenantIcon, removeTenantIcon]);

    const rows = useMemo(() => [tenantConfiguration], [tenantConfiguration]);

    return (
        <>
            <h3 className="text-h3 text-balance">
                <FormattedMessage id="configuration.tenant-icon-control" />
            </h3>
            <DataTable<TenantConfigurationDto>
                columns={columns}
                data={rows}
                columnConfigId="configuration-tenant-icon"
            />
        </>
    );
}
